//Get all permissions from belongGroup

import ls from "localstorage-slim";
import swal from "sweetalert";
import ErrorService from "../components/service/error.service";
import TokenService from "../components/service/token.service";
import UserService from "../components/service/user.service";
import { decideAgentEmpty } from "./agentEmptyBreaker";

// Function to get headers for HTTP requests
export const getHeader = (token, extraHeaders = {}) => {
  return {
    withCredentials: true,
    Authorization: `Bearer ${token}`,
    UserGroup: JSON.parse(localStorage.getItem("userGroup")),
    ...extraHeaders,
  };
};

// Function to get headers for HTTP requests
export const getLineHeader = (token, userGroupID, extraHeaders = {}) => {
  return {
    withCredentials: true,
    Authorization: `Bearer ${token}`,
    UserGroup: userGroupID,
    ...extraHeaders,
  };
};

// Function to check if the user group has admin permission
function checkUserGroupHasAdmin(belongGroup) {
  if (!Array.isArray(belongGroup) || belongGroup.length === 0) {
    return false;
  }
  return belongGroup.some((group) => group.name === "admin");
}

// Function to get all permissions for a user group
export async function getAllPermissions(belongGroup) {
  const userID = JSON.parse(localStorage.getItem("userID"));
  let permissionList = new Set();

  // Add admin permissions if user group has admin rights
  if (checkUserGroupHasAdmin(belongGroup)) {
    permissionList.add("manage user groups");
    permissionList.add("manage node groups");
  }

  if (!Array.isArray(belongGroup) || belongGroup.length === 0) {
    return [...permissionList];
  }

  const getPromiss = belongGroup.map((group) =>
    UserService.getGroupMemberPermissions(
      TokenService.getToken(),
      group._id,
      userID,
    ),
  );

  try {
    const response = await Promise.all(getPromiss);
    response.forEach((result) => {
      const tempList = result.data.permissions;
      if (tempList.length > 0) {
        tempList.forEach((item) => permissionList.add(item));
      }
    });
  } catch (error) {
    //console.log(error);
  }

  return [...permissionList];
}

// Function to get the user group data for user profile display
export function getTempBelongGroupData(belongGroup) {
  const tempBelongGroupData = [];

  // Iterate over each group and push to the temporary data array
  if (Array.isArray(belongGroup) && belongGroup.length > 0) {
    for (let i = 0; i < belongGroup.length; i++) {
      const group = belongGroup[i];
      const permissions = group.permissions.toString().replace(/,/g, " / ");
      const isManager = group.isManager.toString();

      const tempGroup = {
        id: group._id,
        name: group.name,
        permission: permissions,
        isManager: isManager,
      };

      tempBelongGroupData.push(tempGroup);
    }
  }

  return tempBelongGroupData;
}

export function removeItems(itemList, itemsToRemove) {
  return itemList.filter((item) => !itemsToRemove.includes(item));
}

// Function to check if two arrays are equal
export function arraysEqual(a, b) {
  const setA = new Set(a);
  const setB = new Set(b);
  // If the sizes of both sets are not equal, then the arrays are not equal
  if (setA.size !== setB.size) {
    return false;
  }
  // For each item in setA, check if setB also has the item
  for (const item of setA) {
    if (!setB.has(item)) {
      return false;
    }
  }
  // If we passed the previous checks, the arrays are equal
  return true;
}
// Function to check if a certain permission exists in a set of permissions
export function checkPermission(permissions, permission, history) {
  // If permissions include the given permission, return true
  if (permissions.includes(permission)) {
    //console.log(permissions);
    return true;
  } else {
    // If not, show a permission warning
    permissionWarnings(history);
  }
}

// Function to display a permission warning and redirect to the dashboard
export function permissionWarnings(history) {
  swal(
    "It seems you don't have permission to access this page.",
    "Please contact your administrator.",
    "warning",
  ).then(() => {
    // Redirect to the dashboard after the user acknowledges the warning
    history.push("/dashboard");
  });
}

// Cap auto-retries so a long server outage cannot keep firing requests forever.
// Reason: prior behavior retried every 5s/10s without limit. A simple counter
// is not enough here because many widgets (Dashboard, polling pages, etc.)
// share these globals — when the counter hit the cap and reset, the next
// widget's failure would just start a fresh 1..MAX cycle, producing an
// endless 1..5,1..5 loop. We use a sticky cooldown: once MAX retries fire,
// further Network Error responses are silently dropped until the cooldown
// expires, after which a single fresh batch is allowed.
const MAX_NETWORK_RETRIES = 5;
const NETWORK_RETRY_DELAY_MS = 5000;
const NETWORK_BREAKER_COOLDOWN_MS = 60_000;
const MAX_AGENT_EMPTY_RETRIES = 5;
const AGENT_EMPTY_RETRY_DELAY_MS = 10000;
const AGENT_BREAKER_COOLDOWN_MS = 60_000;
let networkRetryCount = 0;
let networkBreakerExpiresAt = 0;
// "Agent Empty" is a per-node/per-endpoint onboarding state, so each request
// gets its OWN retry budget + breaker, keyed by request URL. A shared global
// counter was wrong here: one HostInfo page fans out several /agent_info
// requests at once, and they drained a single budget in one concurrent batch
// — tripping the cooldown long before any endpoint got its 5 retries.
const agentEmptyState = new Map(); // requestKey -> { retryCount, breakerExpiresAt }

// Stable key for a request across its retries. agent_info endpoints carry the
// node id + type in the URL, so the URL distinguishes each one.
function agentRequestKey(error) {
  const cfg =
    (error && error.config) ||
    (error && error.response && error.response.config);
  return cfg && cfg.url ? `${cfg.method || "get"} ${cfg.url}` : "unknown";
}

// Function to handle errors
export function handleError(error, history, retryFunction) {
  ErrorService.handleResponseError(error)
    .then((response) => {
      // Any response other than "Network Error" means the request reached the
      // server, so clear the (global) network breaker + counter.
      if (response !== "Network Error") {
        networkRetryCount = 0;
        networkBreakerExpiresAt = 0;
      }
      // A non-"Agent Empty" response from THIS request means its endpoint
      // answered, so clear only ITS breaker state — never an unrelated node's.
      if (response !== "Agent Empty") {
        agentEmptyState.delete(agentRequestKey(error));
      }

      // Handle specific types of responses
      if (response === "Session expired") {
        swal(
          "Your session has expired!",
          "Please login again.",
          "warning",
        ).then(() => {
          localStorage.clear();
          // Redirect to login page
          history.push("/");
        });
      } else if (response === "Refresh token success") {
        // Retry the function (guard against handleError(error) callers
        // that didn't pass a retryFunction)
        if (typeof retryFunction === "function") retryFunction();
      } else if (response === "Not Login") {
        // console.log("Not Login");
        if (!JSON.parse(localStorage.getItem("change_Token"))) {
          swal(
            "It looks like you aren't logged in.",
            "Please log in to see this page.",
            "warning",
          ).then(() => {
            localStorage.clear();
            history.push("/");
          });
        }
      } else if (response === "Duplicate.") {
        //file duplicate
        //console.log("file duplicate");
        swal("Error!", "Duplicated name.", "error");
      } else if (response === "Verify signature failed.") {
        swal("Error!", "Upload Certificate Fail", "error");
      } else if (response === "Agent Empty") {
        // Initially the Agent info is empty; wait until the node first checks
        // in. Without a retryFunction there is nothing to retry, so bail out
        // before touching breaker state (no pollution, no no-op timer).
        if (typeof retryFunction !== "function") return;
        const key = agentRequestKey(error);
        const { action, state } = decideAgentEmpty(
          agentEmptyState.get(key),
          Date.now(),
          {
            maxRetries: MAX_AGENT_EMPTY_RETRIES,
            cooldownMs: AGENT_BREAKER_COOLDOWN_MS,
          },
        );
        agentEmptyState.set(key, state);
        if (action === "cooldown") {
          console.warn(
            `Agent Empty [${key}]: cooling down for ${
              AGENT_BREAKER_COOLDOWN_MS / 1000
            }s after ${MAX_AGENT_EMPTY_RETRIES} attempts`,
          );
          return;
        }
        if (action === "wait") return;
        setTimeout(retryFunction, AGENT_EMPTY_RETRY_DELAY_MS);
      } else if (response === "Network Error") {
        const now = Date.now();
        if (now < networkBreakerExpiresAt) {
          return;
        }
        if (networkRetryCount >= MAX_NETWORK_RETRIES) {
          console.warn(
            `Network error: cooling down for ${
              NETWORK_BREAKER_COOLDOWN_MS / 1000
            }s after ${MAX_NETWORK_RETRIES} attempts`,
          );
          networkBreakerExpiresAt = now + NETWORK_BREAKER_COOLDOWN_MS;
          networkRetryCount = 0;
          return;
        }
        networkRetryCount++;
        // Linear backoff capped at 4x to avoid runaway delays.
        const delay = NETWORK_RETRY_DELAY_MS * Math.min(networkRetryCount, 4);
        console.log(
          `Network error, retrying in ${
            delay / 1000
          }s (attempt ${networkRetryCount}/${MAX_NETWORK_RETRIES})`,
        );
        setTimeout(() => {
          if (typeof retryFunction === "function") retryFunction();
        }, delay);
      } else if (response === "Action Unavailable Error") {
        ConcurrentError.showWarning();
      } else if (response === "Permission Denied") {
        swal(
          "Permission Denied",
          "You don't have permission to perform this action.",
          "warning",
        );
      } else if (response === "Not Found") {
        swal(
          "Not Found",
          "The requested resource could not be found.",
          "warning",
        );
      } else if (response === "Rate Limited") {
        swal(
          "Too Many Requests",
          "Please slow down and try again in a moment.",
          "warning",
        );
      } else if (response === "Service Unavailable") {
        swal(
          "Service Unavailable",
          "The server is temporarily unavailable. Please try again later.",
          "warning",
        );
      } else {
        if (!JSON.parse(localStorage.getItem("change_Token"))) {
          swal(
            "Something went wrong!",
            "Please contact your administrator.",
            "warning",
          );
        }
      }
    })
    .catch((error) => {
      console.log(error);
    });
}

export function checkUserLogin(history) {
  if (localStorage.getItem("userID")) {
    return true;
  } else {
    swal(
      "It looks like you aren't logged in.",
      "Please log in to see this page.",
      "warning",
    ).then(() => {
      history.push("/");
    });
  }
}

// enable encryption
// ls.config.encrypt = true;
// set a global secret
// ls.config.secret = "1234566";

export function lsEncrypt(key, value) {
  ls.set(key, value, { encrypt: true });
  //ls.set(key, value);
}

export function lsDecrypt(key) {
  return ls.get(key, { decrypt: true });
  // return ls.get(key);
}

export const ConcurrentWarnings = {
  showWarning: function () {
    return swal({
      title: "Concurrent Action Notice",
      content: {
        element: "div",
        attributes: {
          style: "text-align:center;",
          textContent:
            "This feature has been activated by another user. However, the same feature has been automatically activated for you.",
        },
      },
      icon: "info",
    });
  },
};

export const ConcurrentError = {
  showWarning: function () {
    swal(
      "Concurrent Action Error!",
      "Please wait until the task is completed by others.",
      "error",
    );
  },
};

export const FunctionDeactivedWarning = {
  showWarning: function () {
    return swal({
      title: "Function Deactivated",
      content: {
        element: "div",
        attributes: {
          style: "text-align:center;",
          textContent:
            "This feature has been deactivated by another user. If you want to enable it, please reactivate it.",
        },
      },
      icon: "warning",
    });
  },
};
